import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import { FiCheck } from 'react-icons/fi';

const { FiSave, FiBook, FiTrendingUp, FiX, FiInfo, FiFilter, FiUsers, FiDownload, FiFileText } = FiIcons;

const CoursePerformance = () => {
  const { state, dispatch, performanceService } = useApp();
  const [performanceData, setPerformanceData] = useState({
    gradeDistribution: [],
    passFailStats: { pass: 0, fail: 0 },
    cloAchievement: [],
    ploAchievement: []
  });
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showBellCurveGuide, setShowBellCurveGuide] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [suggestions, setSuggestions] = useState([
    'Review teaching materials for topics with low performance',
    'Consider additional tutorial sessions for challenging concepts',
    'Revise assessment difficulty based on bell curve distribution'
  ]);
  // Add state for class group filtering
  const [studentGroups, setStudentGroups] = useState({});
  const [activeGroup, setActiveGroup] = useState('All Groups');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [reportGenLoading, setReportGenLoading] = useState(false);

  useEffect(() => {
    if (state.activeCourse) {
      loadExistingData();
      // Organize students by class groups when component loads
      organizeStudentGroups();
    }
    if (state.studentsData.length > 0) {
      // Set filtered students initially to all students
      setFilteredStudents(state.studentsData);
    }
  }, [state.studentsData, state.activeCourse]);

  // Calculate performance data when filtered students change
  useEffect(() => {
    if (filteredStudents.length > 0) {
      calculatePerformanceData();
    }
  }, [filteredStudents]);

  const organizeStudentGroups = () => {
    const groups = { 'All Groups': [] };
    
    state.studentsData.forEach(student => {
      const group = student.classGroup || student.class_group || 'Ungrouped';
      const faculty = student.faculty || '';
      
      // Add to All Groups
      groups['All Groups'].push(student);
      
      // Initialize group if it doesn't exist
      if (!groups[group]) {
        groups[group] = [];
      }
      
      // Add student to their group
      groups[group].push(student);
    });
    
    setStudentGroups(groups);
  };

  const switchClassGroup = (groupName) => {
    setActiveGroup(groupName);
    
    if (groupName === 'All Groups') {
      setFilteredStudents(state.studentsData);
    } else if (studentGroups[groupName]) {
      setFilteredStudents(studentGroups[groupName]);
    }
  };

  const loadExistingData = async () => {
    if (!state.activeCourse) return;
    setLoading(true);
    try {
      // First try to load from the new table
      const { data, error } = await performanceService.getCommentsAndSuggestions(
        state.activeCourse.id,
        'course_performance'
      );
      
      if (data) {
        setComments(data.comments || '');
        setSuggestions(data.suggestions || suggestions);
        console.log('Loaded comments and suggestions from new table:', data);
      } else {
        // If not found in new table, try the old table for backward compatibility
        const oldData = await performanceService.getPerformanceAnalysis(
          state.activeCourse.id,
          'course_performance'
        );
        
        if (!oldData.error && oldData.data) {
          setComments(oldData.data.comments || '');
          if (oldData.data.data?.suggestions) {
            setSuggestions(oldData.data.data.suggestions);
          }
          console.log('Loaded comments from old table (backward compatibility)');
        }
      }
    } catch (error) {
      console.error('Error loading performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Utility: fetch actual achievement data from state or props, or keep as empty array
  const getCLOAchievement = () => state.cloAchievement || [];
  const getPLOAchievement = () => state.ploAchievement || [];
  
  const calculatePerformanceData = () => {
    const students = filteredStudents;
    const assessments = state.assessmentPlan;

    // Calculate grade distribution with precise GPA points
    const gradeRanges = [
      { grade: 'A+', min: 90, max: 100, gpa: 4.000, count: 0 },
      { grade: 'A', min: 80, max: 89, gpa: 4.000, count: 0 },
      { grade: 'A-', min: 75, max: 79, gpa: 3.700, count: 0 },
      { grade: 'B+', min: 70, max: 74, gpa: 3.300, count: 0 },
      { grade: 'B', min: 65, max: 69, gpa: 3.000, count: 0 },
      { grade: 'B-', min: 60, max: 64, gpa: 2.700, count: 0 },
      { grade: 'C+', min: 55, max: 59, gpa: 2.300, count: 0 },
      { grade: 'C', min: 50, max: 54, gpa: 2.000, count: 0 },
      { grade: 'C-', min: 45, max: 49, gpa: 1.700, count: 0 },
      { grade: 'D', min: 40, max: 44, gpa: 1.000, count: 0 },
      { grade: 'F', min: 0, max: 39, gpa: 0.000, count: 0 }
    ];

    let passCount = 0;
    let failCount = 0;

    students.forEach(student => {
      const total = assessments.reduce((sum, assessment) => {
        return sum + (student.marks[assessment.id] || 0);
      }, 0);

      const gradeRange = gradeRanges.find(range => total >= range.min && total <= range.max);
      if (gradeRange) {
        gradeRange.count++;
      }

      if (total >= 40) {
        passCount++;
      } else {
        failCount++;
      }
    });

    setPerformanceData({
      gradeDistribution: gradeRanges,
      passFailStats: { pass: passCount, fail: failCount },
      cloAchievement: calculateCLOAchievement(),
      ploAchievement: calculatePLOAchievement()
    });
  };

  const calculateCLOAchievement = () => {
    // This would be implemented based on your actual CLO data structure
    // For now, returning empty array or mock data
    return [];
  };

  const calculatePLOAchievement = () => {
    // This would be implemented based on your actual PLO data structure
    // For now, returning empty array or mock data
    return [];
  };

  // Add suggestion
  const addSuggestion = () => {
    setSuggestions([...suggestions, '']);
  };

  // Update suggestion
  const updateSuggestion = (index, value) => {
    const newSuggestions = [...suggestions];
    newSuggestions[index] = value;
    setSuggestions(newSuggestions);
  };

  // Remove suggestion
  const removeSuggestion = (index) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  const saveData = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setSaveLoading(true);
    try {
      // Save to the new table
      const { error: newError } = await performanceService.saveCommentsAndSuggestions(
        state.activeCourse.id,
        'course_performance',
        comments,
        suggestions
      );
      
      if (newError) throw new Error(newError);
      
      // For backward compatibility and to maintain previous functionality, 
      // also save to the old table
      const analysisData = {
        performanceData,
        gradeDistribution: performanceData.gradeDistribution,
        passFailStats: performanceData.passFailStats,
        cloAchievement: performanceData.cloAchievement,
        ploAchievement: performanceData.ploAchievement,
        suggestions: suggestions
      };

      const { error } = await performanceService.savePerformanceAnalysis(
        state.activeCourse.id,
        'course_performance',
        analysisData,
        comments,
        '', // removed preparedBy
        ''  // removed verifiedBy
      );

      if (error) throw new Error(error);
      alert('Course performance analysis saved successfully!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving course performance analysis: ' + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  // Calculate bell curve statistics
  const calculateBellCurveStats = () => {
    const total = performanceData.gradeDistribution.reduce((sum, grade) => sum + grade.count, 0);
    
    if (total === 0) return {};
    
    const highGrades = performanceData.gradeDistribution
      .filter(grade => ['A+', 'A', 'A-'].includes(grade.grade))
      .reduce((sum, grade) => sum + grade.count, 0);
      
    const midGrades = performanceData.gradeDistribution
      .filter(grade => ['B+', 'B', 'B-', 'C+', 'C'].includes(grade.grade))
      .reduce((sum, grade) => sum + grade.count, 0);
      
    const lowGrades = performanceData.gradeDistribution
      .filter(grade => ['C-', 'D', 'F'].includes(grade.grade))
      .reduce((sum, grade) => sum + grade.count, 0);

    return {
      total,
      highGrades,
      midGrades,
      lowGrades,
      highPercentage: Math.round((highGrades / total) * 100),
      midPercentage: Math.round((midGrades / total) * 100),
      lowPercentage: Math.round((lowGrades / total) * 100)
    };
  };

  // Generate comprehensive report
  const generateComprehensiveReport = async (format) => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }
    
    setReportGenLoading(true);
    try {
      // Get groups to include
      const groupsToInclude = activeGroup === 'All Groups' 
        ? ['All Groups'] 
        : [activeGroup];
      
      // Generate report
      const { data, error } = await performanceService.generateComprehensiveReport(
        state.activeCourse.id,
        {
          includeClassGroups: true,
          selectedGroups: groupsToInclude
        }
      );
      
      if (error) throw new Error(error);
      
      // Process report data for export
      console.log('Report generated:', data);
      
      // In a real implementation, this would call a service to generate PDF/Excel
      // For now, we'll just show an alert
      alert(`${format.toUpperCase()} report generated successfully with ${Object.keys(data.groupReports).length} class group(s)!`);
      
      // Close modal
      setShowReportModal(false);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report: ' + error.message);
    } finally {
      setReportGenLoading(false);
    }
  };

  const bellCurveStats = calculateBellCurveStats();

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to view performance analysis.</p>
        </div>
      </motion.div>
    );
  }

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading performance data...</p>
        </div>
      </motion.div>
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Course Info Header */}
      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-indigo-900 mb-2">
              {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-indigo-700">Faculty:</span>
                <p className="text-indigo-600">{state.activeCourse.faculty}</p>
              </div>
              <div>
                <span className="font-medium text-indigo-700">Programme:</span>
                <p className="text-indigo-600">{state.activeCourse.programme}</p>
              </div>
              <div>
                <span className="font-medium text-indigo-700">Semester:</span>
                <p className="text-indigo-600">{state.activeCourse.semester}</p>
              </div>
              <div>
                <span className="font-medium text-indigo-700">Session:</span>
                <p className="text-indigo-600">{state.activeCourse.session}</p>
              </div>
            </div>
          </div>
          
          {/* New: Report Button */}
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <SafeIcon icon={FiFileText} className="w-4 h-4" />
            <span>Comprehensive Report</span>
          </button>
        </div>
      </div>

      {/* Class Group Selection Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-indigo-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Filter by Class Group</h2>
          </div>
          <div className="text-sm text-indigo-600 font-medium">
            {Object.keys(studentGroups).length > 0 && (
              <span>{Object.keys(studentGroups).length - 1} group(s) available</span>
            )}
          </div>
        </div>
        
        {/* Class Group Tabs */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {Object.keys(studentGroups).map(groupName => (
              <button
                key={groupName}
                onClick={() => switchClassGroup(groupName)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeGroup === groupName 
                    ? 'bg-indigo-600 text-white'
                    : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                }`}
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                  <span>{groupName}</span>
                  <span className="ml-2 bg-white text-indigo-800 text-xs px-2 py-0.5 rounded-full">
                    {studentGroups[groupName].length}
                  </span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Active Group Info */}
          {activeGroup && activeGroup !== 'All Groups' && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-indigo-900">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 inline mr-2" />
                    {activeGroup} Class Group
                  </h3>
                  <p className="text-sm text-indigo-700 mt-1">
                    Showing analysis for {studentGroups[activeGroup]?.length || 0} students
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Group Selection Info */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-600" />
            <p className="text-blue-800 text-sm">
              Select a class group to view performance analysis for specific student groups. 
              Select "All Groups" to view combined analysis.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Course Performance Analysis
            {activeGroup !== 'All Groups' && (
              <span className="ml-2 text-lg text-indigo-600">({activeGroup})</span>
            )}
          </h2>
          <button
            onClick={() => setShowBellCurveGuide(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <SafeIcon icon={FiInfo} className="w-4 h-4" />
            <span>Bell Curve Guide</span>
          </button>
        </div>

        {/* Grade Distribution Chart */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grade Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData.gradeDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="grade" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Bell Curve Analysis */}
        {bellCurveStats.total > 0 && (
          <div className="mb-8 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900 mb-4">Bell Curve Distribution Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-green-800">High Achievers (A+, A, A-)</h4>
                <p className="text-2xl font-bold text-green-600">{bellCurveStats.highGrades}</p>
                <p className="text-sm text-green-600">{bellCurveStats.highPercentage}% of students</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="h-2 rounded-full bg-green-600" style={{ width: `${bellCurveStats.highPercentage}%` }}></div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-blue-800">Average Performers (B+, B, B-, C+, C)</h4>
                <p className="text-2xl font-bold text-blue-600">{bellCurveStats.midGrades}</p>
                <p className="text-sm text-blue-600">{bellCurveStats.midPercentage}% of students</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="h-2 rounded-full bg-blue-600" style={{ width: `${bellCurveStats.midPercentage}%` }}></div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-red-800">Below Average (C-, D, F)</h4>
                <p className="text-2xl font-bold text-red-600">{bellCurveStats.lowGrades}</p>
                <p className="text-sm text-red-600">{bellCurveStats.lowPercentage}% of students</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="h-2 rounded-full bg-red-600" style={{ width: `${bellCurveStats.lowPercentage}%` }}></div>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white rounded-lg border border-purple-200">
              <p className="text-purple-800 text-sm">
                <strong>Distribution Pattern:</strong> {
                  bellCurveStats.midPercentage >= 60 ? "Normal distribution - Most students perform at average level" :
                    bellCurveStats.highPercentage >= 50 ? "Positive skew - Course may be too easy or excellent teaching" :
                      bellCurveStats.lowPercentage >= 50 ? "Negative skew - Course may be too difficult or needs intervention" :
                        "Irregular distribution - Further analysis recommended"
                }
              </p>
            </div>
          </div>
        )}

        {/* Grade Distribution Table with 3 decimal GPA */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grade Statistics</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left">Grade</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Marks Range</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">GPA Point</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Quantity</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {performanceData.gradeDistribution.map((grade, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-4 py-2 font-medium">{grade.grade}</td>
                    <td className="border border-gray-300 px-4 py-2">{grade.min}-{grade.max}</td>
                    <td className="border border-gray-300 px-4 py-2 font-mono">{grade.gpa.toFixed(3)}</td>
                    <td className="border border-gray-300 px-4 py-2">{grade.count}</td>
                    <td className="border border-gray-300 px-4 py-2">
                      {bellCurveStats.total > 0 ? `${Math.round((grade.count / bellCurveStats.total) * 100)}%` : '0%'}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2 font-medium">Barred</td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                  <td className="border border-gray-300 px-4 py-2">0</td>
                  <td className="border border-gray-300 px-4 py-2">0%</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2 font-medium">Absent</td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                  <td className="border border-gray-300 px-4 py-2">0</td>
                  <td className="border border-gray-300 px-4 py-2">0%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Pass/Fail Statistics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pass/Fail Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800">Pass</h4>
              <p className="text-2xl font-bold text-green-600">{performanceData.passFailStats.pass}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-800">Fail</h4>
              <p className="text-2xl font-bold text-red-600">{performanceData.passFailStats.fail}</p>
            </div>
          </div>
        </div>

        {/* CLO Achievement */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CLO Achievement</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData.cloAchievement}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="clo" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="achieved" fill="#10B981" name="Achieved" />
              <Bar dataKey="notAchieved" fill="#EF4444" name="Not Achieved" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Comments Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Performance Analysis Comments</h3>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            className="w-full h-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your analysis and comments on the course performance..."
          />
        </div>

        {/* Suggestions for Improvement */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Suggestions for Improvement</h3>
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">{index + 1}.</span>
                <input
                  type="text"
                  value={suggestion}
                  onChange={(e) => updateSuggestion(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter suggestion for improvement"
                />
                <button
                  onClick={() => removeSuggestion(index)}
                  className="text-red-600 hover:text-red-800 px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={addSuggestion}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add Suggestion
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveData}
            disabled={saveLoading}
            className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {saveLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} className="w-4 h-4" />
            )}
            <span>{saveLoading ? 'Saving...' : 'Save Performance Analysis'}</span>
          </button>
        </div>

        {/* Help Section */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">💡 Analysis Guidelines:</h4>
          <ul className="text-blue-800 space-y-1 text-sm">
            <li>• Review grade distribution for balance and fairness</li>
            <li>• Analyze pass/fail ratios to identify potential issues</li>
            <li>• Examine CLO achievement patterns for curriculum effectiveness</li>
            <li>• Consider bell curve distribution for course difficulty assessment</li>
            <li>• Provide detailed comments for future course improvements</li>
            <li>• Save your analysis to preserve institutional records</li>
            <li>• Use class group filtering to compare performance across different groups</li>
          </ul>
        </div>
      </div>

      {/* Bell Curve Guide Modal */}
      <AnimatePresence>
        {showBellCurveGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowBellCurveGuide(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">Understanding Bell Curve in Grade Distribution</h2>
                <button
                  onClick={() => setShowBellCurveGuide(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Bell Curve Guide Content - Added content */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">What is a Bell Curve?</h3>
                  <p className="text-gray-700 mb-3">
                    A bell curve, also known as a normal distribution, is a statistical concept that describes 
                    how data is distributed around a mean (average). In academic grading, it represents the 
                    distribution of student performance, with most students expected to cluster around the average.
                  </p>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p className="text-purple-800 text-sm">
                      In a perfect bell curve, approximately 68% of students should fall within one standard 
                      deviation of the mean (typically the B and C range), while about 16% would be in the 
                      higher range (A's) and 16% in the lower range (D's and F's).
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Interpreting Your Grade Distribution</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2">Normal Distribution (Bell-shaped)</h4>
                      <div className="flex items-start">
                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                          <div className="w-6 h-6 rounded-full bg-blue-600"></div>
                        </div>
                        <div>
                          <p className="text-gray-700 text-sm">
                            When most students (around 60-70%) perform at average levels (B and C ranges), with fewer 
                            students at the high and low ends. This typically indicates appropriate course difficulty 
                            and effective teaching.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2">Positive Skew (Right Skew)</h4>
                      <div className="flex items-start">
                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                          <div className="w-6 h-6 rounded-full bg-green-600"></div>
                        </div>
                        <div>
                          <p className="text-gray-700 text-sm">
                            When a large proportion of students (&gt;50%) achieve high grades (A range). This may indicate 
                            that the course is too easy, assessments are not challenging enough, or that teaching has 
                            been exceptionally effective.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2">Negative Skew (Left Skew)</h4>
                      <div className="flex items-start">
                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                          <div className="w-6 h-6 rounded-full bg-red-600"></div>
                        </div>
                        <div>
                          <p className="text-gray-700 text-sm">
                            When a large proportion of students (&gt;50%) receive low grades (D and F range). This may 
                            indicate that the course is too difficult, teaching methods are ineffective, or students 
                            lack necessary prerequisites.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2">Bimodal Distribution</h4>
                      <div className="flex items-start">
                        <div className="bg-gray-100 p-2 rounded-lg mr-3">
                          <div className="w-6 h-6 rounded-lg bg-purple-600"></div>
                        </div>
                        <div>
                          <p className="text-gray-700 text-sm">
                            When grades cluster at two distinct points (e.g., many A's and F's, but few B's, C's, and D's). 
                            This may indicate a divided class where some students grasp the material well while others 
                            struggle significantly.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Recommended Actions</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3 flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">For Normal Distribution</h4>
                        <p className="text-sm text-gray-700">
                          Continue with current teaching methods and assessment strategies. Consider minor 
                          adjustments to address specific areas where students may be struggling.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-green-100 p-2 rounded-lg mr-3 flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">For Positive Skew (Many High Grades)</h4>
                        <p className="text-sm text-gray-700">
                          Consider increasing assessment difficulty or complexity. Introduce more challenging 
                          topics or higher-order thinking requirements in assignments and exams.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-red-100 p-2 rounded-lg mr-3 flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">For Negative Skew (Many Low Grades)</h4>
                        <p className="text-sm text-gray-700">
                          Review teaching methods and provide additional support resources. Consider adjusting 
                          assessment difficulty, offering remedial sessions, or providing more practice opportunities.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3 flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">For Bimodal Distribution</h4>
                        <p className="text-sm text-gray-700">
                          Identify the differences between high and low-performing groups. Consider offering 
                          differentiated instruction, peer mentoring, or targeted interventions for struggling students.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-2">Important Considerations</h3>
                  <ul className="text-indigo-800 space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 mt-1.5"></span>
                      <span>Bell curve distributions should be used as analytical tools, not as strict grading mandates.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 mt-1.5"></span>
                      <span>Small class sizes may not follow standard distribution patterns.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 mt-1.5"></span>
                      <span>Consider institutional grading policies and standards when analyzing distributions.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 mt-1.5"></span>
                      <span>Always combine statistical analysis with qualitative assessment of student learning.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comprehensive Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Generate Comprehensive Report</h3>
              
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                <div className="flex items-center mb-3">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mr-2" />
                  <h4 className="font-medium text-blue-900">Report Details</h4>
                </div>
                <p className="text-blue-800 text-sm mb-3">
                  This comprehensive report will include:
                </p>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Overall course performance statistics
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Individual student performance details
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Class group analysis ({activeGroup === 'All Groups' ? 'all groups' : activeGroup})
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Assessment achievement metrics
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Grade distribution statistics
                  </li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => generateComprehensiveReport('pdf')}
                  disabled={reportGenLoading}
                  className="w-full p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors text-left flex items-center space-x-3"
                >
                  <SafeIcon icon={FiFileText} className="w-6 h-6 text-red-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">PDF Report</h4>
                    <p className="text-sm text-gray-600">Complete report in PDF format for printing and sharing</p>
                  </div>
                </button>
                
                <button
                  onClick={() => generateComprehensiveReport('excel')}
                  disabled={reportGenLoading}
                  className="w-full p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors text-left flex items-center space-x-3"
                >
                  <SafeIcon icon={FiDownload} className="w-6 h-6 text-green-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Excel Spreadsheet</h4>
                    <p className="text-sm text-gray-600">Data in Excel format for further analysis</p>
                  </div>
                </button>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
              
              {reportGenLoading && (
                <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  <p className="text-indigo-700">Generating your report...</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CoursePerformance;